import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2, Wrench, Split, Plus, Trash2 } from 'lucide-react';
import { logMaterialCost } from '@/lib/jobCostingData';
import {
  JOB_BUCKETS,
  resolveBucketJob,
  isBucketOption,
  formatUsd,
} from '@/lib/jobsData';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const STORES = ['Menards', 'Lowes', 'Home Depot', 'Ace Hardware', 'Other'];

let _matSeq = 0;
const itemId = () => {
  _matSeq += 1;
  return `mat-${Date.now().toString(36)}-${_matSeq}`;
};

export const LogMaterialDialog = ({
  open,
  onOpenChange,
  job,
  jobs = [],
  onLogged,
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState('single'); // 'single' | 'split'
  const [pickedJobId, setPickedJobId] = useState(job?.id || '');
  const [store, setStore] = useState('Menards');
  const [desc, setDesc] = useState('');
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [items, setItems] = useState([]); // [{ id, desc, amount, jobId }]
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (open) {
      setMode('single');
      setPickedJobId(job?.id || '');
      setStore('Menards');
      setDesc('');
      setAmount('');
      setDate(new Date().toISOString().slice(0, 10));
      setItems([]);
      setError('');
    }
  }, [open, job]);

  const itemsTotal =
    Math.round(items.reduce((s, it) => s + (Number(it.amount) || 0), 0) * 100) /
    100;
  const distinctJobs = new Set(
    items.filter((it) => it.jobId && Number(it.amount) > 0).map((it) => it.jobId)
  ).size;
  const allItemsAssigned =
    items.length > 0 &&
    items.every((it) => it.jobId && Number(it.amount) > 0);

  /* --------------------------------------------------------------------- */
  /* Single-item submit (unchanged behavior)                               */
  /* --------------------------------------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    let targetJob;
    if (!isBucketOption(pickedJobId)) {
      targetJob = jobs.find((j) => j.id === pickedJobId) || job;
      if (!targetJob?.id) {
        setError(t('jobCosting.materials.pickJob'));
        return;
      }
    }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError(t('jobCosting.materials.positiveAmount'));
      return;
    }
    setSubmitting(true);
    try {
      if (isBucketOption(pickedJobId)) targetJob = await resolveBucketJob(pickedJobId);
      const updated = await logMaterialCost(targetJob, {
        store,
        desc,
        amount: amt,
        date,
      });
      toast.success(t('jobCosting.materials.loggedToast'));
      onLogged?.(updated);
      onOpenChange?.(false);
    } catch (err) {
      setError(err?.message || t('jobCosting.materials.logFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  /* --------------------------------------------------------------------- */
  /* Split across jobs                                                     */
  /* --------------------------------------------------------------------- */
  const enterSplit = () => {
    setError('');
    if (items.length === 0) {
      setItems([
        { id: itemId(), desc, amount, jobId: pickedJobId || '' },
      ]);
    } else if (pickedJobId) {
      setItems((cur) => cur.map((it) => (it.jobId ? it : { ...it, jobId: pickedJobId })));
    }
    setMode('split');
  };
  const updateItem = (id, patch) =>
    setItems((cur) => cur.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  const removeItem = (id) => setItems((cur) => cur.filter((it) => it.id !== id));
  const addItem = () =>
    setItems((cur) => [
      ...cur,
      { id: itemId(), desc: '', amount: '', jobId: pickedJobId || '' },
    ]);

  const handleSaveSplit = async (e) => {
    e?.preventDefault?.();
    setError('');
    const priced = items.filter((it) => Number(it.amount) > 0);
    if (priced.length === 0) {
      setError('Add at least one item with an amount.');
      return;
    }
    if (!priced.every((it) => it.jobId)) {
      setError('Assign every item to a job before saving.');
      return;
    }
    setSubmitting(true);
    try {
      // Keep the freshest job object per id so multiple items to the SAME job
      // accumulate correctly (each logMaterialCost returns the updated job).
      const jobCache = new Map();
      let logged = 0;
      for (const it of priced) {
        const key = it.jobId;
        let target = jobCache.get(key);
        if (!target) {
          target = isBucketOption(key)
            ? await resolveBucketJob(key)
            : jobs.find((j) => j.id === key);
        }
        if (!target?.id) continue;
        const updated = await logMaterialCost(target, {
          store,
          desc: it.desc,
          amount: Number(it.amount),
          date,
        });
        jobCache.set(key, updated);
        onLogged?.(updated);
        logged += 1;
      }
      const jobsHit = jobCache.size;
      toast.success(
        `Logged ${logged} item${logged > 1 ? 's' : ''} across ${jobsHit} job${
          jobsHit > 1 ? 's' : ''
        }`
      );
      onOpenChange?.(false);
    } catch (err) {
      setError(err?.message || t('jobCosting.materials.logFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const jobSelect = (value, onChange, testid) => (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger
        className={cn(
          'h-10 bg-[hsl(var(--muted))] border-border',
          !value && 'border-[hsl(var(--primary))]/50 text-[hsl(var(--primary))]'
        )}
        data-testid={testid}
      >
        <SelectValue placeholder={t('jobCosting.materials.selectJob')} />
      </SelectTrigger>
      <SelectContent className="bg-[hsl(var(--card))] border-border max-h-72">
        {jobs.map((j) => (
          <SelectItem key={j.id} value={j.id}>
            {j.name}
          </SelectItem>
        ))}
        {JOB_BUCKETS.map((b) => (
          <SelectItem key={b.option} value={b.option}>
            {b.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--hairline))] max-w-md max-h-[92vh] overflow-y-auto"
        data-testid="log-material-dialog"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[hsl(var(--primary))]">
            <Wrench size={16} strokeWidth={2} />
            <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
              {t('jobCosting.materials.logEyebrow')}
            </span>
          </div>
          <DialogTitle className="text-foreground tracking-tight">
            {mode === 'split' ? 'Split across jobs' : t('jobCosting.materials.logTitle')}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {mode === 'split'
              ? 'Add each item and assign it to the job it belongs to — logged as a material cost per job.'
              : t('jobCosting.materials.logSubtitle')}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={mode === 'split' ? handleSaveSplit : handleSubmit}
          className="space-y-4 mt-1"
          noValidate
        >
          {/* Store — applies to the whole run */}
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {t('jobCosting.materials.store')} *
            </Label>
            <Select value={store} onValueChange={setStore}>
              <SelectTrigger
                className="h-10 bg-[hsl(var(--muted))] border-border"
                data-testid="log-material-store"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[hsl(var(--card))] border-border">
                {STORES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {mode === 'single' ? (
            <>
              {/* Job */}
              <div className="space-y-2">
                <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  {t('jobCosting.materials.job')} *
                </Label>
                {jobSelect(pickedJobId, setPickedJobId, 'log-material-job')}
                <button
                  type="button"
                  onClick={enterSplit}
                  className="inline-flex items-center gap-1.5 text-[11px] text-[hsl(var(--primary))] hover:underline underline-offset-4"
                  data-testid="material-enter-split"
                >
                  <Split size={12} />
                  Bought for more than one job? Split across jobs
                </button>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  {t('jobCosting.materials.amount')} *
                </Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                  data-testid="log-material-amount"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  {t('jobCosting.materials.description')}
                </Label>
                <Input
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  placeholder={t('jobCosting.materials.descPlaceholder')}
                  className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
                  data-testid="log-material-desc"
                />
              </div>
            </>
          ) : (
            <>
              {/* Split item rows */}
              <div className="flex items-center justify-between">
                <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  Items
                </Label>
                <button
                  type="button"
                  onClick={addItem}
                  className="inline-flex items-center gap-1 text-[11px] text-[hsl(var(--primary))] hover:underline underline-offset-4"
                  data-testid="material-split-add"
                >
                  <Plus size={12} /> Add item
                </button>
              </div>
              <div className="space-y-2" data-testid="material-split-items">
                {items.map((it) => (
                  <div
                    key={it.id}
                    className="rounded-md border border-border bg-[hsl(var(--muted))]/30 p-2 space-y-2"
                  >
                    <div className="flex items-center gap-2">
                      <Input
                        value={it.desc}
                        onChange={(e) => updateItem(it.id, { desc: e.target.value })}
                        placeholder="Item"
                        className="h-9 flex-1 bg-[hsl(var(--card))] border-border text-foreground text-sm"
                      />
                      <Input
                        type="number"
                        inputMode="decimal"
                        min="0"
                        step="0.01"
                        value={it.amount}
                        onChange={(e) => updateItem(it.id, { amount: e.target.value })}
                        placeholder="0.00"
                        className="h-9 w-24 bg-[hsl(var(--card))] border-border text-foreground text-sm tabular-nums"
                      />
                      <button
                        type="button"
                        onClick={() => removeItem(it.id)}
                        className="text-muted-foreground hover:text-[hsl(var(--destructive))] p-1.5 shrink-0"
                        aria-label="Remove item"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                    <Select
                      value={it.jobId || undefined}
                      onValueChange={(v) => updateItem(it.id, { jobId: v })}
                    >
                      <SelectTrigger
                        className={cn(
                          'h-9 bg-[hsl(var(--card))] border-border text-sm',
                          !it.jobId &&
                            'border-[hsl(var(--primary))]/50 text-[hsl(var(--primary))]'
                        )}
                      >
                        <SelectValue placeholder="Assign to a job…" />
                      </SelectTrigger>
                      <SelectContent className="bg-[hsl(var(--card))] border-border max-h-64">
                        {jobs.map((j) => (
                          <SelectItem key={j.id} value={j.id}>
                            {j.name}
                          </SelectItem>
                        ))}
                        {JOB_BUCKETS.map((b) => (
                          <SelectItem key={b.option} value={b.option}>
                            {b.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))}
                {items.length === 0 ? (
                  <div className="text-center text-xs text-muted-foreground py-4">
                    No items yet — tap “Add item” to start.
                  </div>
                ) : null}
              </div>

              {/* Total + per-job count */}
              <div className="flex items-center justify-between text-[11px] rounded-md px-3 py-2 border border-border text-muted-foreground bg-[hsl(var(--muted))]/30">
                <span>
                  {items.filter((it) => Number(it.amount) > 0).length} item
                  {items.filter((it) => Number(it.amount) > 0).length === 1 ? '' : 's'}
                  {distinctJobs > 0
                    ? ` · ${distinctJobs} job${distinctJobs > 1 ? 's' : ''}`
                    : ''}
                </span>
                <span className="tabular-nums text-foreground font-semibold">
                  {formatUsd(itemsTotal)}
                </span>
              </div>

              <button
                type="button"
                onClick={() => {
                  setMode('single');
                  setError('');
                }}
                className="text-[11px] text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
                data-testid="material-split-back"
              >
                ← Back to a single item
              </button>
            </>
          )}

          {/* Date — applies to the whole run */}
          <div className="space-y-2">
            <Label className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
              {t('jobCosting.materials.date')}
            </Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="h-10 bg-[hsl(var(--muted))] border-border text-foreground"
              data-testid="log-material-date"
            />
          </div>

          {error ? (
            <Alert
              className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
              data-testid="log-material-error"
            >
              <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          ) : null}

          <DialogFooter className="gap-2 sm:gap-3 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange?.(false)}
              disabled={submitting}
              className="text-muted-foreground hover:text-foreground hover:bg-[hsl(var(--muted))]"
            >
              {t('jobCosting.materials.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={submitting || (mode === 'split' && !allItemsAssigned)}
              className="bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.16em] text-xs"
              data-testid="log-material-submit"
            >
              {submitting ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
              {mode === 'split' ? 'Save split' : t('jobCosting.materials.logMaterialBtn')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default LogMaterialDialog;
